import React, { useState, useEffect, useCallback } from 'react';
import { UsuarioListItem, Rol } from '../types/usuario.types';
import { usuarioService } from '../services/usuarioService';
import UsuarioFormPage from './UsuarioFormPage';
import MatriculaFormPage from './MatriculaFormPage';
import AsignacionFormPage from './AsignacionFormPage';
import { useAuth } from 'contexts/AuthContext';

// ============================================================
// UsuariosPage – Lista de usuarios con botones contextuales
// ============================================================

type Vista = 'list' | 'new' | 'edit' | 'matricula' | 'asignacion';

const ROL_LABEL: Record<Rol, string> = {
  ADMIN:'Admin', DIRECTOR:'Director', SECRETARIA:'Secretaría',
  DOCENTE:'Docente', ESTUDIANTE:'Estudiante', PADRE:'Padre',
  AUXILIAR:'Auxiliar', PSICOLOGO:'Psicólogo',
};
const ROL_COLOR: Record<Rol, string> = {
  ADMIN:      'bg-red-100 text-red-700',
  DIRECTOR:   'bg-purple-100 text-purple-700',
  SECRETARIA: 'bg-pink-100 text-pink-700',
  DOCENTE:    'bg-blue-100 text-blue-700',
  ESTUDIANTE: 'bg-green-100 text-green-700',
  PADRE:      'bg-orange-100 text-orange-700',
  AUXILIAR:   'bg-teal-100 text-teal-700',
  PSICOLOGO:  'bg-indigo-100 text-indigo-700',
};

const ROLES_FILTER = [
  { value: '',           label: 'Todos los roles'  },
  { value: 'ADMIN',      label: 'Administrador'    },
  { value: 'DIRECTOR',   label: 'Director'         },
  { value: 'SECRETARIA', label: 'Secretaría'       },
  { value: 'DOCENTE',    label: 'Docente'          },
  { value: 'ESTUDIANTE', label: 'Estudiante'       },
  { value: 'PADRE',      label: 'Padre de Familia' },
  { value: 'AUXILIAR',   label: 'Auxiliar'         },
  { value: 'PSICOLOGO',  label: 'Psicólogo'        },
];

// ── Íconos compactos ──────────────────────────────────────────
const Ico = ({ d, cls = 'w-4 h-4' }: { d: string; cls?: string }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" className={cls}><path d={d} /></svg>
);
const D_PLUS   = 'M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z';
const D_SEARCH = 'M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z';
const D_EDIT   = 'M5.433 13.917l1.262-3.155A4 4 0 0 1 7.58 9.42l6.92-6.918a2.121 2.121 0 0 1 3 3l-6.92 6.918c-.383.383-.84.685-1.343.886l-3.154 1.262a.5.5 0 0 1-.65-.65ZM3.5 5.75c0-.69.56-1.25 1.25-1.25H10A.75.75 0 0 0 10 3H4.75A2.75 2.75 0 0 0 2 5.75v9.5A2.75 2.75 0 0 0 4.75 18h9.5A2.75 2.75 0 0 0 17 15.25V10a.75.75 0 0 0-1.5 0v5.25c0 .69-.56 1.25-1.25 1.25h-9.5c-.69 0-1.25-.56-1.25-1.25v-9.5Z';
const D_DEL    = 'M8.75 1A2.75 2.75 0 0 0 6 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 1 0 .23 1.482l.149-.022.841 10.518A2.75 2.75 0 0 0 7.596 19h4.807a2.75 2.75 0 0 0 2.742-2.53l.841-10.52.149.023a.75.75 0 0 0 .23-1.482A41.03 41.03 0 0 0 14 4.193V3.75A2.75 2.75 0 0 0 11.25 1h-2.5ZM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4ZM8.58 7.72a.75.75 0 0 0-1.5.06l.3 7.5a.75.75 0 1 0 1.5-.06l-.3-7.5Zm4.34.06a.75.75 0 1 0-1.5-.06l-.3 7.5a.75.75 0 1 0 1.5.06l.3-7.5Z';
const D_BOOK   = 'M10.394 2.08a1 1 0 0 0-.788 0l-7 3a1 1 0 0 0 0 1.84L5.25 8.051a.999.999 0 0 1 .356-.257l4-1.714a1 1 0 1 1 .788 1.838L7.667 9.088l1.94.831a1 1 0 0 0 .787 0l7-3a1 1 0 0 0 0-1.838l-7-3Z';
const D_CLIP   = 'M15.98 1.804a1 1 0 0 0-1.96 0l-.24 1.192A1 1 0 0 1 12.8 4H10a1 1 0 0 0 0 2h2.8a3 3 0 0 0 2.94-2.404l.24-1.192ZM4.02 1.804a1 1 0 0 1 1.96 0l.24 1.192A1 1 0 0 0 7.2 4H10a1 1 0 1 1 0 2H7.2a3 3 0 0 1-2.94-2.404L4.02 1.804ZM0 10a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-6Zm9 1a1 1 0 1 0 2 0 1 1 0 0 0-2 0Zm-4 0a1 1 0 1 0 2 0 1 1 0 0 0-2 0Zm8 0a1 1 0 1 0 2 0 1 1 0 0 0-2 0Z';

const SpinnerIcon = () => (
  <svg className="animate-spin w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

const BTN_COLOR: Record<string, string> = {
  blue:   'hover:text-blue-600   hover:bg-blue-50',
  green:  'hover:text-green-600  hover:bg-green-50',
  indigo: 'hover:text-indigo-600 hover:bg-indigo-50',
  red:    'hover:text-red-500    hover:bg-red-50',
};

const ActionBtn: React.FC<{ title: string; color: string; onClick: () => void; children: React.ReactNode }> =
  ({ title, color, onClick, children }) => (
    <button onClick={onClick} title={title}
      className={`p-1.5 rounded-lg text-slate-400 transition-colors ${BTN_COLOR[color]}`}>
      {children}
    </button>
  );

// ── Componente principal ──────────────────────────────────────
const UsuariosPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const esDirector = currentUser?.rol === 'DIRECTOR';
  const [vista, setVista]               = useState<Vista>('list');
  const [editId, setEditId]             = useState<number | null>(null);
  const [selectedUser, setSelectedUser] = useState<UsuarioListItem | null>(null);

  const [usuarios, setUsuarios]         = useState<UsuarioListItem[]>([]);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState<string | null>(null);

  // Filtros
  const [search, setSearch]             = useState('');
  const [rolFilter, setRolFilter]       = useState('');
  const [activoFilter, setActivoFilter] = useState('');   // '' | 'true' | 'false'
  const [page, setPage]                 = useState(0);
  const [totalPages, setTotalPages]     = useState(1);
  const [totalItems, setTotalItems]     = useState(0);

  // Eliminación
  const [deleteId, setDeleteId]         = useState<number | null>(null);
  const [deleting, setDeleting]         = useState(false);

  // ── Cargar lista ──────────────────────────────────────────
  const loadUsuarios = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params: Parameters<typeof usuarioService.listar>[0] = {
        page, size: 10,
        ...(rolFilter    && { rol: rolFilter }),
        ...(search       && { busqueda: search }),
        ...(activoFilter && { activo: activoFilter === 'true' }),
      };
      const res = await usuarioService.listar(params);
      setUsuarios(res.content);
      setTotalPages(res.page.totalPages);
      setTotalItems(res.page.totalElements);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar usuarios');
    } finally { setLoading(false); }
  }, [page, rolFilter, search, activoFilter]);

  useEffect(() => { loadUsuarios(); }, [loadUsuarios]);

  const usuariosPagina = usuarios;
  const pageSeguro     = page;

  const handleSuccess = () => { setVista('list'); loadUsuarios(); };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try { await usuarioService.eliminar(deleteId); setDeleteId(null); loadUsuarios(); }
    catch { /* error handling */ } finally { setDeleting(false); }
  };

  // ── Redirigir a sub-páginas ───────────────────────────────
  if (vista === 'new' || vista === 'edit') {
    return <UsuarioFormPage
      idUsuario={vista === 'edit' ? editId! : undefined}
      onSuccess={handleSuccess} onCancel={() => setVista('list')} />;
  }
  if (vista === 'matricula' && selectedUser) {
    return <MatriculaFormPage
      estudiante={selectedUser}
      onSuccess={handleSuccess} onCancel={() => setVista('list')} />;
  }
  if (vista === 'asignacion' && selectedUser) {
    return <AsignacionFormPage
      docente={selectedUser}
      onSuccess={handleSuccess} onCancel={() => setVista('list')} />;
  }

  // ── Vista lista ───────────────────────────────────────────
  return (
    <div className="space-y-5">

      {/* Cabecera */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Gestión de Usuarios</h1>
          <p className="text-slate-400 text-sm mt-0.5">
            {totalItems} usuario{totalItems !== 1 ? 's' : ''}
            {search.trim() ? ` encontrado${totalItems !== 1 ? 's' : ''}` : ` registrado${totalItems !== 1 ? 's' : ''}`}
          </p>
        </div>
        <button onClick={() => setVista('new')}
          className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white
                     font-semibold text-sm px-4 py-2.5 rounded-xl transition-colors shadow-sm">
          <Ico d={D_PLUS} /> Registrar Usuario
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              <Ico d={D_SEARCH} />
            </span>
            <input type="text" placeholder="Buscar por nombre, DNI o usuario..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(0); }}

              className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50
                         text-sm text-slate-800 placeholder-slate-300 outline-none
                         focus:ring-2 focus:ring-blue-500 transition-all" />
          </div>
          <select value={rolFilter}
            onChange={e => { setRolFilter(e.target.value); setPage(0); }}
            className="px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm
                       text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 min-w-[160px]">
            {ROLES_FILTER.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <select value={activoFilter}
            onChange={e => { setActivoFilter(e.target.value); setPage(0); }}
            className="px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm
                       text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 min-w-[130px]">
            <option value="">Todos</option>
            <option value="true">Activos</option>
            <option value="false">Inactivos</option>
          </select>
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-16"><SpinnerIcon /></div>
        ) : error ? (
          <div className="text-center py-16 text-red-500 text-sm">{error}</div>
        ) : usuarios.length === 0 ? (
          <div className="text-center py-16">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}
              className="w-12 h-12 text-slate-200 mx-auto mb-3">
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"/>
            </svg>
            <p className="text-slate-400 text-sm font-medium">No se encontraron usuarios</p>
            <p className="text-slate-300 text-xs mt-1">Cambia los filtros o registra uno nuevo</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  {['Usuario', 'DNI', 'Contacto', 'Rol', 'Estado', 'Último Acceso', 'Acciones'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {usuariosPagina.map(u => (
                  <tr key={u.idUsuario} className="hover:bg-slate-50 transition-colors">

                    {/* Usuario */}
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center font-bold text-xs bg-blue-100 text-blue-700">
                          {u.nombres.charAt(0)}{u.apellidos.charAt(0)}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800 text-sm leading-tight">
                            {u.nombres} {u.apellidos}
                          </p>
                          <p className="text-slate-400 text-xs">@{u.username}</p>
                        </div>
                      </div>
                    </td>

                    {/* DNI */}
                    <td className="px-4 py-3.5 text-sm text-slate-600 whitespace-nowrap">
                      {u.dni}
                    </td>

                    {/* Contacto (email + teléfono) */}
                    <td className="px-4 py-3.5">
                      <p className="text-sm text-slate-600 leading-tight">{u.email}</p>
                      {u.telefono && (
                        <p className="text-xs text-slate-400 mt-0.5">{u.telefono}</p>
                      )}
                    </td>

                    {/* Rol */}
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${ROL_COLOR[u.rol]}`}>
                        {ROL_LABEL[u.rol]}
                      </span>
                    </td>

                    {/* Estado (activo/inactivo) */}
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full
                        ${u.activo ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${u.activo ? 'bg-green-500' : 'bg-slate-400'}`} />
                        {u.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>

                    {/* Último acceso */}
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      {u.ultimoAcceso ? (
                        <div>
                          <p className="text-xs text-slate-600">{u.ultimoAcceso.split(' ')[0]}</p>
                          <p className="text-xs text-slate-400">{u.ultimoAcceso.split(' ')[1]}</p>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-300">—</span>
                      )}
                    </td>

                    {/* Acciones contextuales por rol */}
                    <td className="px-4 py-3.5">
                      {esDirector && u.rol === 'ADMIN' ? (
                        <span className="text-xs text-slate-300 italic px-2">Sin acceso</span>
                      ) : (
                        <div className="flex items-center gap-0.5">
                          <ActionBtn title="Editar usuario" color="blue"
                            onClick={() => { setEditId(u.idUsuario); setVista('edit'); }}>
                            <Ico d={D_EDIT} />
                          </ActionBtn>
                          {u.rol === 'ESTUDIANTE' && (
                            <ActionBtn title="Gestionar matrícula" color="green"
                              onClick={() => { setSelectedUser(u); setVista('matricula'); }}>
                              <Ico d={D_BOOK} />
                            </ActionBtn>
                          )}
                          {u.rol === 'DOCENTE' && (
                            <ActionBtn title="Asignar cursos y secciones" color="indigo"
                              onClick={() => { setSelectedUser(u); setVista('asignacion'); }}>
                              <Ico d={D_CLIP} />
                            </ActionBtn>
                          )}
                          <ActionBtn title="Eliminar usuario" color="red"
                            onClick={() => setDeleteId(u.idUsuario)}>
                            <Ico d={D_DEL} />
                          </ActionBtn>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Paginación */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 bg-slate-50">
            <p className="text-xs text-slate-400">
              Página {pageSeguro + 1} de {totalPages} · {totalItems} resultado{totalItems !== 1 ? 's' : ''}
              {search.trim() && ` (filtrando por "${search.trim()}")`}
            </p>
            <div className="flex gap-2">
              <button disabled={pageSeguro === 0} onClick={() => setPage(p => p - 1)}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200
                           text-slate-600 disabled:opacity-40 hover:bg-slate-100 transition-colors">
                ← Anterior
              </button>
              <button disabled={pageSeguro >= totalPages - 1} onClick={() => setPage(p => p + 1)}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200
                           text-slate-600 disabled:opacity-40 hover:bg-slate-100 transition-colors">
                Siguiente →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal confirmación de eliminación */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="bg-red-50 border-b border-red-100 px-6 py-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-600">
                  <Ico d={D_DEL} />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800">Eliminar Usuario</h3>
                  <p className="text-slate-400 text-xs">ID: {deleteId}</p>
                </div>
              </div>
            </div>
            <div className="px-6 py-5">
              <p className="text-slate-600 text-sm">
                ¿Estás seguro? Esta acción no se puede deshacer.
              </p>
            </div>
            <div className="px-6 pb-5 flex gap-3">
              <button onClick={() => setDeleteId(null)} disabled={deleting}
                className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold
                           text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors">
                Cancelar
              </button>
              <button onClick={confirmDelete} disabled={deleting}
                className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-sm font-bold
                           text-white transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {deleting && <SpinnerIcon />}
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsuariosPage;